import { Link } from "react-router-dom";
import { ShieldCheck, ArrowLeft } from "lucide-react";

const Section = ({ title, children }) => (
  <section className="mb-8">
    <h2 className="text-lg font-semibold text-white mb-3">{title}</h2>
    <div className="text-zinc-400 text-sm leading-relaxed space-y-2">{children}</div>
  </section>
);

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-background noise" data-testid="privacy-policy-page">
      <div className="max-w-3xl mx-auto px-6 py-12">
        <Link to="/" className="inline-flex items-center gap-2 text-zinc-500 hover:text-primary text-sm mb-8">
          <ArrowLeft className="w-4 h-4" /> Torna alla home
        </Link>

        <div className="flex items-center gap-3 mb-2">
          <ShieldCheck className="w-8 h-8 text-primary" />
          <h1 className="text-3xl font-bold text-white">Informativa sulla Privacy</h1>
        </div>
        <p className="text-zinc-500 text-sm mb-10">
          Ai sensi degli artt. 13 e 14 del Regolamento UE 2016/679 (GDPR) — Ultimo aggiornamento: giugno 2026
        </p>

        <Section title="1. Titolare del trattamento">
          <p>
            Il titolare del trattamento dei dati raccolti tramite la piattaforma <strong className="text-white">PServicePro</strong> (pservicepro.it)
            è il gestore della piattaforma. Per esercitare i tuoi diritti puoi utilizzare le funzioni in-app
            (Impostazioni → Privacy e Dati) o contattare l'assistenza dalla piattaforma.
          </p>
          <p>
            Per i dati dei clienti finali (automobilisti) inseriti dalle officine, ciascuna officina è titolare
            autonoma del trattamento e la piattaforma agisce come responsabile ai sensi dell'art. 28 GDPR.
          </p>
        </Section>

        <Section title="2. Dati trattati">
          <ul className="list-disc pl-5 space-y-1">
            <li>Dati di registrazione: nome, username, email, password (conservata esclusivamente in forma cifrata)</li>
            <li>Dati aziendali: ragione sociale, partita IVA, codice fiscale, indirizzo, telefono, PEC, codice SDI</li>
            <li>Dati operativi: preventivi, fatture, appuntamenti, ordini, dati dei veicoli e dei clienti dell'officina</li>
            <li>Dati RENTRI: formulari rifiuti (FIR), certificati digitali caricati dall'utente</li>
            <li>Dati tecnici: log di accesso, sessioni, indirizzo IP (sicurezza e prevenzione abusi)</li>
          </ul>
        </Section>

        <Section title="3. Finalità e basi giuridiche">
          <ul className="list-disc pl-5 space-y-1">
            <li><strong className="text-white">Erogazione del servizio</strong> (art. 6.1.b — contratto): gestione account, preventivi, fatturazione, appuntamenti</li>
            <li><strong className="text-white">Obblighi di legge</strong> (art. 6.1.c): conservazione documenti fiscali (10 anni), registri RENTRI (3 anni)</li>
            <li><strong className="text-white">Sicurezza</strong> (art. 6.1.f — legittimo interesse): protezione account, audit log, prevenzione frodi</li>
          </ul>
        </Section>

        <Section title="4. Conservazione dei dati">
          <ul className="list-disc pl-5 space-y-1">
            <li>Dati account e operativi: fino alla cancellazione dell'account</li>
            <li>Documenti fiscali: 10 anni (art. 2220 c.c., DPR 633/72), anche dopo la cancellazione dell'account, in forma anonimizzata</li>
            <li>Registri e FIR RENTRI: 3 anni (D.Lgs. 152/2006)</li>
            <li>Log tecnici e sessioni: per la sola durata tecnica necessaria</li>
          </ul>
        </Section>

        <Section title="5. Modalità e sicurezza">
          <p>
            I dati sono trattati con strumenti informatici adottando misure di sicurezza adeguate (art. 32 GDPR):
            cifratura delle password (bcrypt), comunicazioni cifrate HTTPS/TLS, storage sicuro non pubblico per i file caricati,
            backup giornalieri, blocco account dopo tentativi di accesso falliti, registrazione delle operazioni amministrative.
          </p>
        </Section>

        <Section title="6. Destinatari e trasferimenti">
          <p>
            I dati non vengono venduti né ceduti a terzi. Possono essere trattati da fornitori tecnici (hosting, invio email
            transazionali) nominati responsabili del trattamento, e trasmessi ad autorità pubbliche ove previsto dalla legge
            (es. sistema RENTRI del Ministero dell'Ambiente per la tracciabilità dei rifiuti, su azione dell'utente).
          </p>
        </Section>

        <Section title="7. I tuoi diritti (artt. 15-22 GDPR)">
          <ul className="list-disc pl-5 space-y-1">
            <li><strong className="text-white">Accesso e portabilità</strong> (artt. 15, 20): scarica tutti i tuoi dati in formato JSON da Impostazioni → Privacy e Dati</li>
            <li><strong className="text-white">Rettifica</strong> (art. 16): modifica i tuoi dati dal profilo in qualsiasi momento</li>
            <li><strong className="text-white">Cancellazione</strong> (art. 17): richiedi la cancellazione dell'account da Impostazioni → Privacy e Dati; elaborazione entro 30 giorni</li>
            <li><strong className="text-white">Limitazione e opposizione</strong> (artt. 18, 21): contatta l'assistenza dalla piattaforma</li>
            <li><strong className="text-white">Reclamo</strong>: hai diritto di proporre reclamo al Garante per la Protezione dei Dati Personali (garanteprivacy.it)</li>
          </ul>
        </Section>

        <Section title="8. Cookie">
          <p>
            La piattaforma utilizza esclusivamente cookie tecnici necessari al funzionamento (gestione della sessione di accesso).
            Non vengono utilizzati cookie di profilazione o di terze parti a fini pubblicitari.
          </p>
        </Section>

        <div className="mt-10 pt-6 border-t border-zinc-800 text-xs text-zinc-600">
          © 2026 PServicePro — Documento soggetto ad aggiornamenti; le modifiche rilevanti saranno comunicate in piattaforma.
        </div>
      </div>
    </div>
  );
}
